/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable quotes */
import React, { useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import keyBy from "lodash/keyBy";

import { stripesConnect, useStripes } from "@folio/stripes/core";

import { useIntl } from "react-intl";
import { clone, cloneDeep } from "lodash";
import { useLocationsForTenants } from "../hooks";
import { DataContext } from "../contexts";
import { OKAPI_TENANT_HEADER } from "../constants";
import { isUserInConsortiumMode } from "../utils";

// Provider which loads dictionary data used in various places in ui-inventory.
// The data is fetched once when the ui-inventory module is loaded.
const DataProvider = ({ children, resources, mutator }) => {
  const { manifest } = DataProvider;
  const dataRef = useRef({});
  const stripes = useStripes();
  const intl = useIntl();

  const { consortium } = stripes.user.user;

  useEffect(() => {
    if (!consortium) {
      return;
    }

    mutator.consortiaTenants.GET({
      path: `consortia/${consortium?.id}/tenants?limit=1000`,
      headers: {
        [OKAPI_TENANT_HEADER]: consortium?.centralTenantId,
      },
    });
  }, [consortium?.id]);

  const tenantIds = resources.consortiaTenants.records.map(
    (tenant) => tenant.id,
  );

  const { data: locationsOfAllTenants } = useLocationsForTenants({ tenantIds });

  useEffect(() => {
    mutator.locations.GET({ tenant: stripes.okapi.tenant });
  }, [stripes.okapi.tenant]);

  const isLoading = useMemo(() => {
    // eslint-disable-next-line guard-for-in
    for (const key in manifest) {
      const isResourceLoading =
        !resources?.[key]?.hasLoaded &&
        !resources?.[key]?.failed &&
        resources?.[key]?.isPending;

      if (manifest[key].type === "okapi" && isResourceLoading) {
        return true;
      }
    }

    return false;
  }, [resources, manifest]);

  useEffect(() => {
    if (isLoading || !dataRef.current) {
      return;
    }

    const loadedData = {};

    Object.keys(manifest).forEach((key) => {
      loadedData[key] = resources?.[key]?.records ?? [];
    });

    if (isUserInConsortiumMode(stripes)) {
      loadedData.locations = locationsOfAllTenants;
    }

    const {
      locations,
      identifierTypes,
      holdingsSources,
      instanceRelationshipTypes,
      statisticalCodeTypes,
      statisticalCodes,
      consortiaTenants,
    } = loadedData;

    loadedData.locationsById = keyBy(locations, "id");
    loadedData.identifierTypesById = keyBy(identifierTypes, "id");
    loadedData.identifierTypesByName = keyBy(identifierTypes, "name");
    loadedData.holdingsSourcesByName = keyBy(holdingsSources, "name");
    loadedData.instanceRelationshipTypesById = keyBy(
      instanceRelationshipTypes,
      "id",
    );
    loadedData.consortiaTenantsById = keyBy(consortiaTenants, "id");
    const statisticalCodeTypesById = keyBy(statisticalCodeTypes, "id");

    // attach full statisticalCodeType object to each statisticalCode
    loadedData.statisticalCodes = statisticalCodes.map((sc) => {
      sc.statisticalCodeType =
        statisticalCodeTypesById[sc.statisticalCodeTypeId];
      return sc;
    });

    /** kware start edit section --
     * Enable dynamic translations for inventory controlled vocabularies
     */
    const translatedData = {};
    Object.keys(loadedData).forEach((table) => {
      const loadedKey = cloneDeep(loadedData[table]);
      for (const key in loadedKey) {
        if (key) {
          loadedKey[key].name = intl.formatMessage({
            id: `ui-inventory.${table}.name.${loadedData[table][key].name}`,
            defaultMessage: loadedData[table][key].name,
          });
        }
      }
      translatedData[table] = loadedKey;
    });
    dataRef.current = translatedData;
    /** kware end edit section */
  }, [resources, manifest, isLoading, dataRef]);

  if (isLoading) {
    return null;
  }

  return (
    <DataContext.Provider value={dataRef.current} key={intl.locale}>
      {children}
    </DataContext.Provider>
  );
};

DataProvider.propTypes = {
  mutator: PropTypes.object.isRequired,
  resources: PropTypes.object.isRequired,
  children: PropTypes.object,
};

DataProvider.manifest = {
  consortiaTenants: {
    type: "okapi",
    records: "tenants",
    accumulate: true,
    throwErrors: false,
  },
  identifierTypes: {
    type: "okapi",
    records: "identifierTypes",
    path: "identifier-types?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  contributorTypes: {
    type: "okapi",
    records: "contributorTypes",
    path: "contributor-types?limit=400&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  contributorNameTypes: {
    type: "okapi",
    records: "contributorNameTypes",
    path: "contributor-name-types?limit=1000&query=cql.allRecords=1 sortby ordering",
    resourceShouldRefresh: true,
  },
  instanceFormats: {
    type: "okapi",
    records: "instanceFormats",
    path: "instance-formats?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  instanceTypes: {
    type: "okapi",
    records: "instanceTypes",
    path: "instance-types?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  classificationTypes: {
    type: "okapi",
    records: "classificationTypes",
    path: "classification-types?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  alternativeTitleTypes: {
    type: "okapi",
    records: "alternativeTitleTypes",
    path: "alternative-title-types?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  locations: {
    type: "okapi",
    records: "locations",
    path: "locations",
    params: {
      limit: (q, p, r, l, props) =>
        props?.stripes?.config?.maxUnpagedResourceCount || 1000,
      query: "cql.allRecords=1 sortby name",
    },
    accumulate: true,
    resourceShouldRefresh: true,
  },
  instanceRelationshipTypes: {
    type: "okapi",
    records: "instanceRelationshipTypes",
    path: "instance-relationship-types?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  instanceStatuses: {
    type: "okapi",
    records: "instanceStatuses",
    path: "instance-statuses?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  modesOfIssuance: {
    type: "okapi",
    records: "issuanceModes",
    path: "modes-of-issuance?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  instanceNoteTypes: {
    type: "okapi",
    path: "instance-note-types",
    params: {
      query: "cql.allRecords=1 sortby name",
      limit: "1000",
    },
    records: "instanceNoteTypes",
    resourceShouldRefresh: true,
  },
  electronicAccessRelationships: {
    type: "okapi",
    records: "electronicAccessRelationships",
    path: "electronic-access-relationships?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  statisticalCodeTypes: {
    type: "okapi",
    records: "statisticalCodeTypes",
    path: "statistical-code-types?limit=1000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  statisticalCodes: {
    type: "okapi",
    records: "statisticalCodes",
    path: "statistical-codes?limit=2000&query=cql.allRecords=1 sortby name",
    resourceShouldRefresh: true,
  },
  illPolicies: {
    type: "okapi",
    path: "ill-policies?limit=1000&query=cql.allRecords=1 sortby name",
    records: "illPolicies",
    resourceShouldRefresh: true,
  },
  holdingsTypes: {
    type: "okapi",
    path: "holdings-types?limit=1000&query=cql.allRecords=1 sortby name",
    records: "holdingsTypes",
    resourceShouldRefresh: true,
  },
  callNumberTypes: {
    type: "okapi",
    path: "call-number-types?limit=1000&query=cql.allRecords=1 sortby name",
    records: "callNumberTypes",
    resourceShouldRefresh: true,
  },
  holdingsNoteTypes: {
    type: "okapi",
    path: "holdings-note-types?limit=1000&query=cql.allRecords=1 sortby name",
    records: "holdingsNoteTypes",
    resourceShouldRefresh: true,
  },
  itemNoteTypes: {
    type: "okapi",
    path: "item-note-types",
    params: {
      query: "cql.allRecords=1 sortby name",
      limit: "1000",
    },
    records: "itemNoteTypes",
    resourceShouldRefresh: true,
  },
  itemDamagedStatuses: {
    type: "okapi",
    path: "item-damaged-statuses?limit=1000&query=cql.allRecords=1 sortby name",
    records: "itemDamageStatuses",
    resourceShouldRefresh: true,
  },
  natureOfContentTerms: {
    type: "okapi",
    path: "nature-of-content-terms?limit=1000&query=cql.allRecords=1 sortby name",
    records: "natureOfContentTerms",
    resourceShouldRefresh: true,
  },
  materialTypes: {
    type: "okapi",
    path: "material-types",
    params: {
      query: "cql.allRecords=1 sortby name",
      limit: "1000",
    },
    records: "mtypes",
    resourceShouldRefresh: true,
  },
  loanTypes: {
    type: "okapi",
    path: "loan-types",
    params: {
      query: "cql.allRecords=1 sortby name",
      limit: "1000",
    },
    records: "loantypes",
    resourceShouldRefresh: true,
  },
  tags: {
    path: "tags?limit=10000", // the same as Tags component in stripes-smart-components
    records: "tags",
    throwErrors: false,
    type: "okapi",
    resourceShouldRefresh: true,
  },
  holdingsSources: {
    type: "okapi",
    path: "holdings-sources",
    params: {
      query: "cql.allRecords=1 sortby name",
      limit: "1000",
    },
    records: "holdingsRecordsSources",
    resourceShouldRefresh: true,
  },
  tenantLocales: {
    type: "okapi",
    records: "locales",
    path: "tenant-locales?limit=1000",
  },
  inventoryTemplates: {
    type: "okapi",
    records: "templates",
    path: "inventory-templates?limit=1000",
  },
  inventoryDefaultFields: {
    type: "okapi",
    records: "defaultFields",
    path: "inventory-defaultFields?limit=1000",
  },
  inventoryCustomFields: {
    type: "okapi",
    records: "customFields",
    path: "inventory-customFields?limit=1000",
  },
  inventoryTemplatesConfigs: {
    type: "okapi",
    records: "templatesConfigs",
    path: "inventory-templates-configs?limit=1000",
  },
};

export default stripesConnect(DataProvider);
